import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { PostType, Post, Attachment, MediaType } from '../types';
import { uploadMediaFile } from '../utils/mediaUtils';

interface UploadState {
    id: string;
    progress: number;
    status: 'uploading' | 'completed' | 'error' | 'cancelled';
    error?: string;
    fileName?: string;
}

interface UploadContextType {
    activeUpload: UploadState | null;
    startUpload: (
        userId: string,
        postData: {
            type: PostType;
            content: Post['content'];
            visibility?: 'public' | 'private';
            showCaption?: boolean;
            themeOpacity?: number;
            fontId?: string;
            postcardStyle?: string;
            postcardShape?: 'square' | 'wide' | 'tall';
        },
        attachments: Attachment[],
        coverImage: { url: string; file?: File } | null,
        onCreatePost: (data: any) => void,
        onUploadFailed?: (localId: number) => void,
        overrideLocalId?: number
    ) => Promise<void>;
    cancelUpload: () => void;
    startGenericUpload: (
        userId: string,
        files: { file: File, type: MediaType, name?: string }[],
        onComplete: (attachments: Attachment[]) => void
    ) => Promise<void>;
    clearUpload: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
};

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeUpload, setActiveUpload] = useState<UploadState | null>(null);
    const xhrRefs = useRef<XMLHttpRequest[]>([]);

    const cancelUpload = useCallback(() => {
        xhrRefs.current.forEach(xhr => xhr.abort());
        xhrRefs.current = [];
        setActiveUpload(prev => prev ? { ...prev, status: 'cancelled' } : null);
        setTimeout(() => setActiveUpload(null), 3000);
    }, []);

    const clearUpload = useCallback(() => {
        setActiveUpload(null);
    }, []);

    const startUpload = useCallback(async (
        userId: string,
        postData: {
            type: PostType;
            content: Post['content'];
            visibility?: 'public' | 'private';
            showCaption?: boolean;
            themeOpacity?: number;
            fontId?: string;
            postcardStyle?: string;
            postcardShape?: 'square' | 'wide' | 'tall';
        },
        attachments: Attachment[],
        coverImage: { url: string; file?: File } | null,
        onCreatePost: (data: any) => void,
        onUploadFailed?: (localId: number) => void,
        overrideLocalId?: number
    ) => {
        const uploadId = overrideLocalId ? overrideLocalId.toString() : Date.now().toString();
        xhrRefs.current = [];

        setActiveUpload({
            id: uploadId,
            progress: 0,
            status: 'uploading',
            fileName: attachments.length > 0 ? (attachments[0].file?.name || 'media') : 'post'
        });

        // Save attachments and coverImage file blobs into IndexedDB in case of failure/refresh
        const { savePendingPostAttachments, deletePendingPostAttachments } = require('../indexedDb');
        if (attachments.length > 0) {
            const filesToSave = attachments.filter(a => a.file).map(a => ({ file: a.file!, type: a.type, isLive: a.isLive }));
            savePendingPostAttachments(Number(uploadId), filesToSave, coverImage ? { file: coverImage.file } : null).catch(console.error);
        }

        // Optimistically call onCreatePost so it shows up in the feed immediately
        onCreatePost({
            ...postData,
            isOptimistic: true,
            localId: Number(uploadId)
        });

        try {
            // Calculate total size for multi-file progress
            const totalSize = attachments.reduce((acc, att) => acc + (att.file?.size || 0), 0) + (coverImage?.file?.size || 0);
            let uploadedBytes = 0;
            const fileProgresses = new Map<number, number>();

            const isLocalUrl = (url: string) => {
                return url.startsWith('blob:') || 
                       url.startsWith('file:') || 
                       url.startsWith('filesystem:') ||
                       url.includes('_capacitor_file_') || 
                       url.startsWith('http://localhost') || 
                       url.startsWith('https://localhost') || 
                       url.startsWith('capacitor:');
            };

            const uploadPromises = attachments.map(async (att, index) => {
                if (att.file && isLocalUrl(att.url)) {
                    const promise = uploadMediaFile(att.file, userId, (p) => {
                        fileProgresses.set(index, (p / 100) * (att.file?.size || 0));
                        const currentTotalUploaded = Array.from(fileProgresses.values()).reduce((a, b) => a + b, 0);
                        const overallProgress = Math.min(Math.round((currentTotalUploaded / totalSize) * 100), 99);
                        setActiveUpload(prev => prev ? { ...prev, progress: overallProgress } : null);
                    });

                    // The modified uploadMediaFile returns a promise with .xhr attached
                    const xhr = (promise as any).xhr;
                    if (xhr) xhrRefs.current.push(xhr);

                    const url = await promise;
                    return { ...att, url };
                }
                return att;
            });

            let uploadedCoverImage = coverImage;
            if (coverImage && coverImage.file && isLocalUrl(coverImage.url)) {
                const promise = uploadMediaFile(coverImage.file, userId, (p) => {
                    // Approximate progress for cover image too if desired
                });
                const xhr = (promise as any).xhr;
                if (xhr) xhrRefs.current.push(xhr);
                const url = await promise;
                uploadedCoverImage = { ...coverImage, url };
            }

            const uploadedAttachments = await Promise.all(uploadPromises);

            setActiveUpload(prev => prev ? { ...prev, progress: 100, status: 'completed' } : null);

            // Finalize post creation
            onCreatePost({
                ...postData,
                isOptimistic: false,
                localId: Number(uploadId),
                content: {
                    ...postData.content,
                    media: uploadedAttachments.map(({ url, type, width, height, file, isLive, originalUrl, name }) => ({
                        url,
                        type,
                        width,
                        height,
                        name: name || file?.name || undefined,
                        isLive: !!isLive,
                        originalUrl
                    })),
                    coverImageUrl: uploadedCoverImage?.url,
                }
            });

            // Clean up and delete pending post attachments from IndexedDB
            deletePendingPostAttachments(Number(uploadId)).catch(console.error);
            setTimeout(() => setActiveUpload(null), 3000);

        } catch (error: any) {
            if (error.message === 'Upload cancelled') {
                console.log('Upload was cancelled by user');
                return;
            }
            console.error('Background upload failed:', error);
            setActiveUpload(prev => prev ? {
                ...prev,
                status: 'error',
                error: error.message || 'Upload failed'
            } : null);
            // Notify the parent so it can mark the post as failed in the feed
            if (onUploadFailed) {
                onUploadFailed(Number(uploadId));
            }
            // Don't auto-clear error so user can see it
        } finally {
            xhrRefs.current = [];
        }
    }, []);

    const startGenericUpload = useCallback(async (
        userId: string,
        files: { file: File, type: MediaType, name?: string }[],
        onComplete: (attachments: Attachment[]) => void
    ) => {
        const uploadId = Date.now().toString();
        xhrRefs.current = [];

        setActiveUpload({
            id: uploadId,
            progress: 0,
            status: 'uploading',
            fileName: files.length > 0 ? (files[0].name || files[0].file.name || 'media') : 'media'
        });

        try {
            const totalSize = files.reduce((acc, f) => acc + (f.file?.size || 0), 0);
            const fileProgresses = new Map<number, number>();

            const uploadPromises = files.map(async (f, index) => {
                const promise = uploadMediaFile(f.file, userId, (p) => {
                    fileProgresses.set(index, (p / 100) * (f.file?.size || 0));
                    const currentTotalUploaded = Array.from(fileProgresses.values()).reduce((a, b) => a + b, 0);
                    const overallProgress = totalSize > 0 ? Math.min(Math.round((currentTotalUploaded / totalSize) * 100), 99) : 100;
                    setActiveUpload(prev => prev ? { ...prev, progress: overallProgress } : null);
                });

                const xhr = (promise as any).xhr;
                if (xhr) xhrRefs.current.push(xhr);

                const url = await promise;
                return { url, type: f.type, name: f.name || f.file.name } as Attachment;
            });

            const uploadedAttachments = await Promise.all(uploadPromises);

            setActiveUpload(prev => prev ? { ...prev, progress: 100, status: 'completed' } : null);

            onComplete(uploadedAttachments);

            setTimeout(() => setActiveUpload(null), 3000);

        } catch (error: any) {
            if (error.message === 'Upload cancelled') {
                console.log('Upload was cancelled by user');
                return;
            }
            console.error('Generic upload failed:', error);
            setActiveUpload(prev => prev ? {
                ...prev,
                status: 'error',
                error: error.message || 'Upload failed'
            } : null);
        } finally {
            xhrRefs.current = [];
        }
    }, []);

    return (
        <UploadContext.Provider value={{ activeUpload, startUpload, cancelUpload, startGenericUpload, clearUpload }}>
            {children}
        </UploadContext.Provider>
    );
};
